import { FC, useState, useCallback, useMemo } from 'react';
import { KatButton, KatTextarea, KatDropdown } from '@amzn/katal-react';
import {
  AssetType,
  Genre,
  ProviderLogo,
  SourceAssetMediaType,
  TransformationVariant,
} from '@amzn/fire-tv-campaign-automation-service';
import { AssetInputHandler } from 'src/handlers/AssetInputHandler';
import { AssetState, AssetStatus } from 'src/types';
import { useSelection } from 'src/contexts/SelectionContext';
import { CTA, getCtaOptions } from 'src/configs/ctaConfiguration';
import { getProviderLogoOptions } from 'src/config/providerLogoConfiguration';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { GENRE_OPTIONS } from 'src/configs/genreConfiguration';
import { getValidationProps } from 'src/configs/assetConfig';
import { TRIVIA_TRAILER_CONSTRAINTS } from 'src/constants';
import {
  MultiImageInput,
  ImageEntry,
  ImageValidationSpec,
} from 'src/components/general/MultiImageInput';
import styles from './TriviaTrailerInput.module.css';

interface TriviaTrailerInputProps {
  disabled?: boolean;
  assetInputHandler: AssetInputHandler;
  setState: (state: Partial<AssetState>) => void;
}

export const TriviaTrailerInput: FC<TriviaTrailerInputProps> = ({
  disabled = false,
  assetInputHandler,
  setState,
}) => {
  const { locale, placement } = useSelection();
  const { assetStates } = useAssetContext();

  const validationProps = getValidationProps(
    AssetType.VIDEO_TRAILER,
    placement,
    TransformationVariant.TRIVIA_TRAILER,
  )!;
  const imageValidationSpec: ImageValidationSpec = {
    maxFileSizeBytes: validationProps.maxFileSizeInBytes,
    minWidth: validationProps.dimensionSpecification.minResolution.width,
    minHeight: validationProps.dimensionSpecification.minResolution.height,
    aspectRatio: validationProps.dimensionSpecification.aspectRatio,
    acceptedFileTypes: TRIVIA_TRAILER_CONSTRAINTS.ACCEPTED_IMAGE_TYPES,
  };
  const [images, setImages] = useState<ImageEntry[]>([]);
  const [triviaText, setTriviaText] = useState('');
  const [genre, setGenre] = useState<Genre | ''>('');
  const [cta, setCta] = useState<CTA | ''>('');
  const [providerLogo, setProviderLogo] = useState<ProviderLogo | ''>('');

  const status = assetStates[AssetType.VIDEO_TRAILER]?.status;
  const isSubmitting = status === AssetStatus.TRANSFORMATION_IN_PROGRESS;
  const isDisabled = disabled || isSubmitting;
  const isFormValid =
    images.length > 0 &&
    triviaText.trim().length > 0 &&
    triviaText.length <= TRIVIA_TRAILER_CONSTRAINTS.MAX_TEXT_LENGTH &&
    genre.length > 0 &&
    cta.length > 0 &&
    providerLogo.length > 0;
  const ctaOptions = useMemo(
    () => getCtaOptions(locale).map((c) => ({ name: c, value: c })),
    [locale],
  );
  const providerLogoOptions = useMemo(() => getProviderLogoOptions(), []);

  const handleCancel = () => {
    setImages([]);
    setTriviaText('');
    setGenre('');
    setCta('');
    setProviderLogo('');
    setState({ status: AssetStatus.PENDING_INPUT });
  };

  const handleSave = useCallback(async () => {
    if (!isFormValid) return;

    try {
      // Resolve files from ImageEntry (file or URL-based)
      const files: File[] = [];
      for (let idx = 0; idx < images.length; idx++) {
        const img = images[idx];
        if (img.file) {
          files.push(img.file);
        } else if (img.url) {
          const resp = await fetch(img.url);
          if (!resp.ok) throw new Error(`Failed to fetch image: ${resp.status}`);
          const blob = await resp.blob();
          const ext = blob.type ? blob.type.split('/')[1] : 'jpg';
          files.push(
            new File([blob], `trivia_image_${idx}.${ext}`, { type: blob.type || 'image/jpeg' }),
          );
        }
      }

      // Upload files and set metadata + sourceAssetKeys + status in a single state update
      // so the transform hook reads consistent state when PENDING_TRANSFORMATION fires
      const sourceAssets = await assetInputHandler.handleMultipleFiles(
        files,
        SourceAssetMediaType.IMAGE,
      );
      setState({
        mediaText: { trivia_text: `Did you know? ${triviaText.trim()}` },
        genres: [genre as Genre],
        cta: cta as CTA,
        providerLogo: providerLogo as ProviderLogo,
        sourceAssetKeys: sourceAssets,
        status: AssetStatus.PENDING_TRANSFORMATION,
      });
    } catch (error) {
      if (status !== AssetStatus.FAILED) {
        const message = error instanceof Error ? error.message : 'Failed to process images';
        setState({ status: AssetStatus.FAILED, error: message });
      }
    }
  }, [
    images,
    triviaText,
    genre,
    cta,
    providerLogo,
    isFormValid,
    assetInputHandler,
    setState,
    status,
  ]);

  return (
    <div className={styles.container} data-testid="trivia-trailer-input">
      <MultiImageInput
        images={images}
        onImagesChange={setImages}
        maxImages={TRIVIA_TRAILER_CONSTRAINTS.MAX_IMAGES}
        validationSpec={imageValidationSpec}
        disabled={isDisabled}
        reorderHint="Drag to reorder. Images appear in this order in the trailer."
        data-testid="trivia-image"
      />

      <KatTextarea
        label="Trivia"
        placeholder="Enter the trivia question or fun fact for the trailer..."
        value={triviaText}
        onInput={(e: KatTextarea.ChangeEvent) => setTriviaText(e.currentTarget.value)}
        disabled={isDisabled}
        data-testid="trivia-text-input"
      />
      {triviaText.length > TRIVIA_TRAILER_CONSTRAINTS.MAX_TEXT_LENGTH && (
        <span className={styles.validationError}>
          Trivia text is too long. Maximum {TRIVIA_TRAILER_CONSTRAINTS.MAX_TEXT_LENGTH} characters
          allowed.
        </span>
      )}
      <KatDropdown
        label="Genre"
        options={GENRE_OPTIONS}
        value={genre}
        onChange={(e: KatDropdown.ChangeEvent) => setGenre(e.detail.value as Genre)}
        placeholder="Select a genre"
        disabled={isDisabled}
        data-testid="trivia-genre-dropdown"
      />
      <KatDropdown
        label="Call To Action"
        options={ctaOptions}
        value={cta}
        onChange={(e: KatDropdown.ChangeEvent) => setCta(e.detail.value as CTA)}
        placeholder="Select CTA"
        disabled={isDisabled}
        data-testid="trivia-cta-dropdown"
      />
      <KatDropdown
        label="Provider Logo"
        options={providerLogoOptions}
        value={providerLogo}
        onChange={(e: KatDropdown.ChangeEvent) => setProviderLogo(e.detail.value as ProviderLogo)}
        placeholder="Select Provider Logo"
        disabled={isDisabled}
        data-testid="trivia-provider-logo-dropdown"
      />
      <div className={styles.buttonContainer}>
        <KatButton
          variant="tertiary"
          onClick={handleCancel}
          disabled={isDisabled}
          data-testid="trivia-cancel-button"
        >
          Cancel
        </KatButton>
        <KatButton
          variant="primary"
          onClick={handleSave}
          disabled={isDisabled || !isFormValid}
          data-testid="trivia-save-button"
        >
          Generate Trailer
        </KatButton>
      </div>
    </div>
  );
};
